import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { Clock } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { prettyCategory } from '../pages/ShopTime';
import DateField from './DateField';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LogShopTimeDialog({ trigger, onLogged }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [technicians, setTechnicians] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({
    tech_id: '',
    date: new Date().toISOString().slice(0, 10),
    hours: '',
    overtime_hours: '',
    category: 'general',
    equipment_id: '',
    description: '',
  });

  const canManageOthers = ['management', 'foreman'].includes(user?.role);

  const fetchAll = useCallback(async () => {
    try {
      const [tRes, eqRes, catRes] = await Promise.all([
        axios.get(`${API}/technicians`),
        axios.get(`${API}/equipment`),
        axios.get(`${API}/shop-time/categories`),
      ]);
      setTechnicians(tRes.data);
      setEquipment(eqRes.data);
      setCategories(catRes.data.categories || []);
      if (user?.role === 'technician') {
        const me = tRes.data.find(
          (t) => (t.name || '').toLowerCase().trim() === (user.name || '').toLowerCase().trim()
        );
        if (me) setForm((f) => ({ ...f, tech_id: me.id }));
      }
    } catch {
      /* toast handled lazily */
    }
  }, [user]);

  useEffect(() => {
    if (open) fetchAll();
  }, [open, fetchAll]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.tech_id || !form.hours || !form.category) {
      toast.error('Tech, hours and category required');
      return;
    }
    if (form.category === 'equipment_maintenance' && !form.equipment_id) {
      toast.error('Select equipment for Equipment Maintenance');
      return;
    }
    try {
      await axios.post(`${API}/shop-time`, {
        tech_id: form.tech_id,
        date: form.date,
        hours: parseFloat(form.hours),
        overtime_hours: form.overtime_hours ? parseFloat(form.overtime_hours) : null,
        category: form.category,
        equipment_id: form.category === 'equipment_maintenance' ? form.equipment_id : null,
        description: form.description.trim() || null,
      });
      toast.success('Shop time logged');
      setOpen(false);
      setForm({
        tech_id: form.tech_id, // keep tech selected for quick repeat entries
        date: form.date,
        hours: '',
        overtime_hours: '',
        category: 'general',
        equipment_id: '',
        description: '',
      });
      if (onLogged) onLogged();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to log');
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button className="bg-slate-900" data-testid="open-shop-time-dialog">
            <Clock size={16} className="mr-1" />
            Log Shop Time
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-lg" data-testid="shop-time-dialog">
        <DialogHeader>
          <DialogTitle>Log Shop Time</DialogTitle>
          <DialogDescription>
            Quick-log non-job time (training, meetings, maintenance, etc.)
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3" data-testid="shop-time-dialog-form">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-500">Technician</Label>
              <Select
                value={form.tech_id}
                onValueChange={(v) => setForm({ ...form, tech_id: v })}
                disabled={user?.role === 'technician'}
              >
                <SelectTrigger data-testid="dlg-tech-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  {technicians
                    .filter((t) =>
                      canManageOthers ||
                      (t.name || '').toLowerCase().trim() === (user?.name || '').toLowerCase().trim()
                    )
                    .map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Date</Label>
              <DateField value={form.date} onChange={(v) => setForm({ ...form, date: v })} data-testid="dlg-date" />
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Category</Label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger data-testid="dlg-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {categories.map((c) => (
                  <SelectItem key={c} value={c}>{prettyCategory(c)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {form.category === 'equipment_maintenance' && (
            <div>
              <Label className="text-xs text-slate-500">Equipment</Label>
              <Select value={form.equipment_id} onValueChange={(v) => setForm({ ...form, equipment_id: v })}>
                <SelectTrigger data-testid="dlg-equipment-select"><SelectValue placeholder="Select equipment" /></SelectTrigger>
                <SelectContent>
                  {equipment.map((e) => (
                    <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-500">Hours</Label>
              <Input
                type="number"
                step="0.25"
                min="0"
                placeholder="e.g. 2.5"
                value={form.hours}
                onChange={(e) => setForm({ ...form, hours: e.target.value })}
                required
                data-testid="dlg-hours"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Overtime</Label>
              <Input
                type="number"
                step="0.25"
                min="0"
                placeholder="optional"
                value={form.overtime_hours}
                onChange={(e) => setForm({ ...form, overtime_hours: e.target.value })}
              />
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Description (optional)</Label>
            <Textarea
              rows={2}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </div>
          <Button type="submit" className="w-full bg-slate-900" data-testid="dlg-submit-btn">Log</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
